/**
 * HintsCard — Vincent B598.d (Eric 2026-07-29 SPEC STRICK)
 *
 * Micro-page « Astuces » dans les paramètres utilisateur.
 * Liste tous les hints one-time gérés par le hook `useOneTimeHint`,
 * affiche leur état (Vue / En attente) et propose de les réafficher
 * individuellement OU en bloc.
 *
 * Pour ajouter un nouvel hint : ajouter une entrée dans `HINTS_REGISTRY`.
 * Convention de clé : `kn-<contexte>-<feature>-hint-seen`.
 */
import { useCallback, useEffect, useState } from "react";
import { Lightbulb, CheckCircle, Clock, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

// Registre central — synchro avec les usages réels dans le code.
const HINTS_REGISTRY = [
  {
    key: "kn-next-action-rotate-hint-seen",
    label_fr: "Prochaine action — bouton rotation",
    label_en: "Next action — rotation button",
    description_fr: "Signale que le bouton bleu à gauche fait défiler les autres suggestions.",
    description_en: "Explains that the blue button on the left cycles through other suggestions.",
    location_fr: "Tableau de bord",
    location_en: "Dashboard",
  },
  {
    key: "kn-agora-archive-hint-seen",
    label_fr: "Messagerie — archiver une conversation",
    label_en: "Messaging — archive a conversation",
    description_fr: "Indique qu'il faut survoler une conversation pour révéler le bouton d'archivage.",
    description_en: "Reveals that hovering a conversation shows the archive button.",
    location_fr: "Messagerie › Conversations",
    location_en: "Messaging › Conversations",
  },
  {
    key: "kn-ext-reindex-hint-seen",
    label_fr: "Ressources externes — réindexation manuelle",
    label_en: "External resources — manual reindex",
    description_fr: "Rappelle qu'un cron auto rafraîchit déjà les ressources chaque dimanche 03 h.",
    description_en: "Reminds that an auto-cron already refreshes resources every Sunday 3 AM.",
    location_fr: "Ressources externes (mode concepteur)",
    location_en: "External resources (designer mode)",
  },
];

function readState() {
  const state = {};
  for (const h of HINTS_REGISTRY) {
    try { state[h.key] = sessionStorage.getItem(h.key) === "1"; }
    catch (_) { state[h.key] = false; }
  }
  return state;
}

export default function HintsCard({ lang = "fr" }) {
  const L = (fr, en) => (lang === "en" ? en : fr);
  const [state, setState] = useState(() => readState());

  useEffect(() => {
    // Reload state after a mount + on storage changes (rare, mais robuste).
    const on = () => setState(readState());
    window.addEventListener("storage", on);
    return () => window.removeEventListener("storage", on);
  }, []);

  const resetOne = useCallback((key) => {
    try { sessionStorage.removeItem(key); } catch (_) { /* private mode */ }
    setState((s) => ({ ...s, [key]: false }));
  }, []);

  const resetAll = useCallback(() => {
    for (const h of HINTS_REGISTRY) {
      try { sessionStorage.removeItem(h.key); } catch (_) { /* ignore */ }
    }
    setState(readState());
  }, []);

  const seenCount = Object.values(state).filter(Boolean).length;
  const total = HINTS_REGISTRY.length;

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="hints-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
            <Lightbulb size={20} className="text-[#002FA7]" weight="fill" />
          </div>
          <div className="min-w-0">
            <h2 className="font-semibold text-lg font-['Outfit']">
              {L("Astuces découvertes", "Discovered tips")}
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5" data-testid="hints-summary">
              {L(
                `${seenCount} astuce(s) déjà vue(s) sur ${total}. Tu peux les réafficher en une fois pour redécouvrir l'application.`,
                `${seenCount} tip(s) already seen out of ${total}. You can restore them all to rediscover the app.`
              )}
            </p>
          </div>
        </div>
        <Button
          onClick={resetAll}
          variant="outline"
          size="sm"
          disabled={seenCount === 0}
          data-testid="hints-reset-all-btn"
          title={L("Réafficher toutes les astuces à la prochaine visite", "Show all tips again on next visit")}
        >
          <ArrowCounterClockwise size={14} className="mr-1.5" />
          {L("Tout réafficher", "Show all again")}
        </Button>
      </div>

      <ul className="space-y-2">
        {HINTS_REGISTRY.map((h) => {
          const seen = state[h.key];
          return (
            <li
              key={h.key}
              className="flex items-start gap-3 py-2 px-3 rounded-lg border border-border/60 bg-background"
              data-testid={`hints-item-${h.key}`}
            >
              <div className="mt-0.5 flex-shrink-0">
                {seen
                  ? <CheckCircle size={18} weight="fill" className="text-teal-500" />
                  : <Clock size={18} className="text-amber-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  {L(h.label_fr, h.label_en)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {L(h.description_fr, h.description_en)}
                </p>
                <p className="text-[11px] text-muted-foreground/70 mt-1">
                  <span className="font-mono">{L("Emplacement :", "Location:")}</span>{" "}
                  {L(h.location_fr, h.location_en)}
                </p>
              </div>
              <Button
                onClick={() => resetOne(h.key)}
                variant="ghost"
                size="sm"
                disabled={!seen}
                className="flex-shrink-0 text-xs"
                data-testid={`hints-reset-${h.key}`}
              >
                {seen ? L("Réafficher", "Restore") : L("En attente", "Pending")}
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
